import {
  BrandPowerStations,
  BrandExtraBatteries,
  PowerStation,
  ExtraBattery,
} from "../../data/types/products";

export type AnyBrandData = BrandPowerStations | BrandExtraBatteries;
export type DataInput = AnyBrandData | Record<string, AnyBrandData>;

export interface GroupBucket {
  systems: any[];
  captions: Array<{ number: number; text: string }>;
  _map?: Map<string, number>;
}
export type GroupedDataResult = Record<string, GroupBucket>;

export interface DataItemsResult {
  systems: any[];
  captions: Array<{ number: number; text: string }>;
}

// Default logic used across the site
const DEFAULT_CONFIG = {
  overrides: {}, // Structure: { "BrandName": ["Item 1", "Item 2"] } OR ["Item 1", "Item 2"]
  overrideProp: null, // The string name of the bool property (e.g., 'highlighted')
  filterFn: null,
  collectCaptions: true, // Single flag to control caption processing

  // Logic to determine which bucket a product belongs to
  getGroupName: (item) => {
    if (item.modular === true) return "modularSystems";
    const dcTotal = (item.dcOutput || 0) + (item.dcOutput2 || 0);

    if (dcTotal > 19) return "powerStationsHighDC";
    if (dcTotal > 0) return "powerStationsLowDC";
    return "powerStationsNoDC";
  },
  // The buckets that should exist in the returned object
  groups: [
    "powerStationsNoDC",
    "powerStationsLowDC",
    "powerStationsHighDC",
    "modularSystems",
  ],
};

// Helper to extract the product collection based on available target key
const getCollection = (brandData) =>
  brandData.powerStations || brandData.extraBatteries || brandData;

// Normalizes, filters, sorts, and groups product data into structured categories with dynamic table captions
const getGroupedData = (
  data: DataInput,
  customConfig = {}
): GroupedDataResult => {
  // Merge defaults with any overrides provided by the page
  const config = { ...DEFAULT_CONFIG, ...customConfig };
  let normalizedList = [];

  const applyCustomProps = (product) => {
    const item = { ...product };

    if (config.overrideProp && config.overrides) {
      // 1. If it's a flat array, use it directly (Single Brand mode)
      // 2. If it's an object, look up the brand key (Registry mode)
      const listForThisBrand = Array.isArray(config.overrides)
        ? config.overrides
        : config.overrides[item.brand];

      if (
        Array.isArray(listForThisBrand) &&
        listForThisBrand.includes(item.name)
      ) {
        item[config.overrideProp] = true;
      }
    }
    return item;
  };

  // 1. Normalization (Single Brand vs Registry)
  if (data.name && (data.powerStations || data.extraBatteries)) {
    normalizedList = Object.values(getCollection(data)).map((product) =>
      applyCustomProps(product)
    );
  } else {
    normalizedList = Object.entries(data).flatMap(([key, brandFile]) => {
      const stations = getCollection(brandFile);
      return Object.values(stations).map((product) =>
        applyCustomProps(product)
      );
    });
  }

  // 2. Filter
  if (config.filterFn) {
    normalizedList = normalizedList.filter(config.filterFn);
  }

  // 3. Sort
  // Allow passing sort: false or a custom sort function in config
  const sortedData =
    config.sort === false
      ? normalizedList
      : typeof config.sort === "function"
        ? normalizedList.sort(config.sort)
        : normalizedList.sort((a, b) => {
            const brandSort = a.brand.localeCompare(b.brand);
            if (brandSort !== 0) return brandSort;
            return (b.capacity || 0) - (a.capacity || 0);
          });

  // 4. Grouping using the Configurable logic
  // Initialize the accumulator based on the groups defined in config
  const initialGroups = config.groups.reduce((acc, name) => {
    acc[name] = { systems: [], captions: [], _map: new Map() };
    return acc;
  }, {});

  const groupedData = sortedData.reduce((acc, item) => {
    // Dynamically determine the group name based on config logic
    const groupName = config.getGroupName(item);

    // Safety: ensure the group exists if getGroupName returns something unexpected
    if (!acc[groupName]) {
      acc[groupName] = { systems: [], captions: [], _map: new Map() };
    }

    const group = acc[groupName];

    // Process Captions only when enabled

    const numberedCaptions = {};
    if (
      config.collectCaptions &&
      item.captions &&
      Object.keys(item.captions).length > 0
    ) {
      for (const [key, text] of Object.entries(item.captions)) {
        if (!group._map.has(text)) {
          const newNumber = group._map.size + 1;
          group._map.set(text, newNumber);
          group.captions.push({ number: newNumber, text });
        }
        numberedCaptions[key] = { number: group._map.get(text), text };
      }
    }

    group.systems.push({
      ...item,
      captions:
        config.collectCaptions && Object.keys(numberedCaptions).length > 0
          ? numberedCaptions
          : null,
    });

    return acc;
  }, initialGroups);

  // Cleanup map helpers
  Object.values(groupedData).forEach((group) => delete group._map);

  return groupedData;
};

// Normalizes input data and extracts a specific, ordered subset of products matching a list of item names
const getDataItems = (
  data: DataInput,
  names: string[] = [],
  customConfig = {}
): DataItemsResult => {
  const config = { ...DEFAULT_CONFIG, ...customConfig };
  let normalizedList = [];

  const applyCustomProps = (product) => {
    const item = { ...product };

    if (config.overrideProp && config.overrides) {
      const listForThisBrand = Array.isArray(config.overrides)
        ? config.overrides
        : config.overrides[item.brand];

      if (
        Array.isArray(listForThisBrand) &&
        listForThisBrand.includes(item.name)
      ) {
        item[config.overrideProp] = true;
      }
    }
    return item;
  };

  // Normalize (same as getGroupedData)
  if (data.name && (data.powerStations || data.extraBatteries)) {
    normalizedList = Object.values(getCollection(data)).map((product) =>
      applyCustomProps(product)
    );
  } else {
    normalizedList = Object.entries(data).flatMap(([key, brandFile]) => {
      const stations = getCollection(brandFile);
      return Object.values(stations).map((product) =>
        applyCustomProps(product)
      );
    });
  }

  // Filter to requested items
  const selected = normalizedList.filter((item) => names.includes(item.name));

  // Sort by the order of the names array
  selected.sort((a, b) => {
    return names.indexOf(a.name) - names.indexOf(b.name);
  });

  // Return in group format
  return {
    systems: selected,
    captions: [],
  };
};

export { getGroupedData, getDataItems };
